import { SEO } from "@/components/SEO";
import { lazy, Suspense } from "react";
import SectionFallback from "@/components/SectionFallback";
import { LazyLoad } from "@/components/LazyLoad";
import { ScrollReveal } from "@/components/ScrollReveal";
import { triggerConsultaPopup } from "@/components/ConsultaPopup";

// Navbar e Hero carregados eagerly — críticos para FCP
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
const StatsCounter = lazy(() => import("@/components/StatsCounter"));
const About = lazy(() => import("@/components/About"));
const Treatments = lazy(() => import("@/components/Treatments"));
const Clinic = lazy(() => import("@/components/Clinic"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const FAQ = lazy(() => import("@/components/FAQ"));
const ScheduleConsultation = lazy(() => import("@/components/ScheduleConsultation"));
const Footer = lazy(() => import("@/components/Footer"));
import InteractiveHoverButton from "@/components/ui/interactive-hover-button";
import { Button } from "@/components/ui/button";

const Index = () => {
  return (
    <div className="min-h-screen bg-background selection:bg-primary/10 selection:text-primary">
      <SEO
        title="Dermatologista em Sorocaba/SP"
        description="Dra. Adriana Rodrigues — Dermatologista especialista em doenças de pele, cabelos e unhas. Atendimento presencial em Sorocaba/SP e teleconsulta para todo o Brasil."
        url="/"
        image="https://assets.cdn.filesafe.space/rc8Fx2cZBlMBT1xVdV7Z/media/6a0e37a1e05851175c1e211e.webp"
        speakable={true}
      />
      <Navbar />
      <main>
        <Hero 
          title={<><span className="font-bold text-black">Dermatologista de referência em </span><span className="italic text-primary font-normal">doenças de pele, cabelos e unhas</span></>}
          className="mt-[40px]" 
          titleClassName="pr-[30px]" 
          containerClassName="container-custom mb-[110px] mt-[60px] md:mt-[90px]" 
        />

        <div>
          <LazyLoad minHeight="400px" fallback={<SectionFallback />}>
            <Suspense fallback={<SectionFallback />}>
              <StatsCounter />
              <ScrollReveal><Treatments /></ScrollReveal>
            </Suspense>
          </LazyLoad>
        </div>

        <div>
          <LazyLoad minHeight="800px" fallback={<SectionFallback />}>
            <Suspense fallback={<SectionFallback />}>
              <About />
              <Testimonials />
            </Suspense>
          </LazyLoad>
        </div>

        <div>
          <ScrollReveal>
            <section className="relative w-full flex items-center justify-center overflow-hidden">
              <div 
                className="absolute inset-0 z-0 bg-cover bg-center bg-fixed"
                style={{ backgroundImage: "url('https://assets.cdn.filesafe.space/rc8Fx2cZBlMBT1xVdV7Z/media/6a0dfff2ca9579c76ba8053f.webp')" }}
              />
              <div className="absolute inset-0 z-0 bg-black/60" />
              <div className="container-custom relative z-10 text-center pt-[80px] pb-[80px] md:pt-[160px] md:pb-[160px]">
                <h2 className="text-3xl md:text-5xl font-serif font-bold text-white mb-6 leading-tight max-w-3xl mx-auto drop-shadow-md">
                  Uma pele bem cuidada é o reflexo da sua saúde.
                </h2>
                <p className="text-lg md:text-xl text-white/90 max-w-2xl mx-auto font-light drop-shadow mb-10">
                  Cuidar da sua pele é um ato de amor próprio e proteção. Conte com o nosso cuidado especializado para todos os tipos de pele.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <InteractiveHoverButton 
                    classes="bg-primary hover:bg-accent text-white text-base font-semibold shadow-lg shadow-primary/20 py-4 px-8 w-full sm:w-auto"
                    onClick={triggerConsultaPopup}
                    hoverContent={
                      <>
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        <span>Abrir WhatsApp</span>
                      </>
                    }
                  >
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    Agendar Consulta
                  </InteractiveHoverButton>

                </div>
              </div>
            </section>
          </ScrollReveal>
        </div>

        <div>
          <LazyLoad minHeight="800px" fallback={<SectionFallback />}>
            <Suspense fallback={<SectionFallback />}>
              <FAQ />
              <Clinic />
              <ScheduleConsultation />
            </Suspense>
          </LazyLoad>
        </div>
      </main>
      <div>
        <LazyLoad minHeight="400px" fallback={<SectionFallback />}>
          <Suspense fallback={<SectionFallback />}>
            <Footer />
          </Suspense>
        </LazyLoad>
      </div>
    </div>
  );
};

export default Index;
